import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  SelectGroup,
} from '@src/components/ui/select'
import { SidebarGuideIcon } from '@app/layout/Sidebar/icons/SidebarGuideIcon'
import { ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { appLink } from '@app/config/appLink'

export type MetricOption<TValue extends string = string> = {
  value: TValue
  label: string
}

export type MetricSelectorProps<TValue extends string = string> = {
  label?: string
  placeholder?: string
  value: TValue
  options: readonly { value: string; label: string }[]
  onValueChange: (value: TValue) => void
  disabled?: boolean
}

const METRIC_DESCRIPTION_MAP: Record<string, string> = {
  damage_cost: 'Estimated cost of damage to exposed elements',
  damage_cost_pc: 'Damage cost as a percentage of total element value',
  exposed_value: 'Total value of exposed elements',
  damage_severity: 'Share of the element stock effectively damaged',
  quantity: 'Physical quantity exposed, in the element type’s unit',
  quantity_pc: 'Share of the total element stock exposed',
}

export const MetricSelector = <TValue extends string = string>({
  label = 'Metric',
  placeholder = 'Select a metric',
  value,
  options,
  onValueChange,
  disabled = false,
}: MetricSelectorProps<TValue>) => {
  const navigate = useNavigate()

  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
      <label className="text-xs font-medium text-foreground">{label}</label>
      <Select
        value={value}
        onValueChange={(nextValue) => onValueChange(nextValue as TValue)}
        disabled={disabled}
      >
        <SelectTrigger className="h-10 w-full rounded-lg bg-background text-sm shadow-sm border border-border">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent align="start">
          <SelectGroup>
            {options.map((option, index) => (
              <SelectItem
                key={option.value + index}
                value={option.value}
                className="text-foreground flex flex-col justify-start content-start items-start"
                description={METRIC_DESCRIPTION_MAP[option.value]}
              >
                {option.label}
              </SelectItem>
            ))}
            <SelectSeparator className="w-full" />
            <div
              onClick={() =>
                navigate(appLink.guidePage.path + '/methods/consequences/consequence-metrics')
              }
              className="font-semibold text-primary flex content-center gap-1 py-1.5 pl-2 pr-8 cursor-pointer"
            >
              <SidebarGuideIcon active height={20} width={20} className="mt-[1px]" />
              <p>Learn more in Guide</p>
              <ArrowRight height={16} width={16} className="mt-[3px]" />
            </div>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
